import PricingBlock from "../components/PricingBlock";
import HeroSearch from "../components/HeroSearch";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, Scale, Wallet, Sparkles, ChevronDown } from "lucide-react";
import { EditableText } from "../lib/content";
import { useSeo } from "../lib/useSeo";

const FAQ = [
  {
    q: "Are prices VAT inclusive?",
    a: [
      "Yes. All prices shown include UK VAT.",
      "Monthly Protection: £15 per month.",
      "Annual Protection: £155.40 per year.",
      "No hidden charges. No surprise fees.",
    ],
  },
  {
    q: "Is there a setup fee?",
    a: [
      "No.",
      "Your subscription costs exactly what you see on the page. There are no setup fees, onboarding fees, or additional administration charges.",
    ],
  },
  {
    q: "What happens if someone copies my business name?",
    a: [
      "Every subscription includes up to £10,000 of passing-off legal assistance.",
      "If another business starts trading under a confusingly similar name, we'll help you understand your options, assist with drafting a response, and contribute towards legal costs if the matter escalates.",
    ],
  },
  {
    q: "Why do businesses choose Business Name Protection?",
    a: [
      "Business Name Protection helps you establish evidence of use from day one, build a record of your trading history, and access support if a dispute arises in the future.",
      "For many businesses, it's a practical alternative where trade mark registration isn't suitable or hasn't yet been pursued.",
    ],
  },
];

const EVERY_PLAN = [
  "Up to £10,000 passing-off legal assistance",
  "Dated record of your business name use",
  "Simple guidance if a naming conflict arises",
  "Domain and online footprint awareness support",
  "Email and phone access to a UK team",
  "Onboarding scan when you join",
  "No hidden fees or surprise charges",
];

export default function Pricing() {
  useSeo({
    title: "Pricing - Business Name Protection from £15/mo",
    description: "Monthly £15 (incl. VAT) or annual £155.40. Same protection both ways - up to £10,000 of passing-off legal assistance, dated proof of use, real UK team. Cancel anytime.",
  });

  return (
    <main data-testid="pricing-page">
      {/* HERO */}
      <section className="relative overflow-hidden bnp-radial-glow pt-14 md:pt-20 pb-10">
        <div className="absolute inset-0 bnp-grid-bg opacity-30 pointer-events-none" />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center relative">
          <span className="bnp-chip bnp-chip-teal">Pricing</span>
          <h1 className="font-display text-[clamp(2rem,4vw,3.4rem)] text-[#04273d] mt-4 font-semibold tracking-tight leading-[1.08]">
            <EditableText keyName="pricing.hero.title" default="Simple protection for your business name. No legal complexity." />
          </h1>
          <p className="font-display text-lg md:text-xl text-[#447694] mt-4 font-semibold tracking-tight leading-snug">
            <EditableText keyName="pricing.hero.tagline" default="Your business name is one of your most valuable assets." />
          </p>
          <p className="text-[#447694] text-base md:text-lg mt-4 leading-relaxed">
            <EditableText keyName="pricing.hero.body" default="Business Name Protection gives you a simple, affordable way to reduce the risk of name confusion, copying, and costly rebranding." />
          </p>
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap text-xs font-display font-semibold uppercase tracking-widest text-[#447694]">
            <span className="inline-flex items-center gap-1.5">
              <Check size={13} className="text-[#0d9b80]" /> All prices include VAT
            </span>
            <span className="text-[#9ac7f7]">&middot;</span>
            <span className="inline-flex items-center gap-1.5">
              <Check size={13} className="text-[#0d9b80]" /> No setup fees
            </span>
            <span className="text-[#9ac7f7]">&middot;</span>
            <span className="inline-flex items-center gap-1.5">
              <Check size={13} className="text-[#0d9b80]" /> Cancel any time
            </span>
          </div>
        </div>
      </section>

      {/* CHOOSE YOUR PLAN - section heading + the two cards */}
      <section className="py-8" data-testid="pricing-plans-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-[#04273d] font-bold tracking-tight">
              <EditableText keyName="pricing.choose.title" default="Choose your plan" />
            </h2>
            <p className="font-display text-base md:text-lg text-[#447694] font-semibold mt-2 tracking-tight">
              <EditableText keyName="pricing.choose.sub" default="Two options. Same protection. Different commitment." />
            </p>
            <p className="text-sm md:text-base text-[#447694] mt-2 leading-relaxed">
              <EditableText keyName="pricing.choose.body" default="Both plans include full Business Name Protection. The only difference is how you prefer to pay." />
            </p>
          </div>
          <PricingBlock />
        </div>
      </section>

      {/* EVERY PLAN INCLUDES */}
      <section className="py-12" data-testid="pricing-every-plan-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="bnp-card p-8 md:p-10">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="h-11 w-11 rounded-2xl bg-[#71edc9]/25 flex items-center justify-center shrink-0">
                <ShieldCheck size={22} className="text-[#04273d]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="bnp-chip bnp-chip-light">What you get with every plan</p>
                <h2 className="font-display text-2xl md:text-3xl text-[#04273d] font-bold tracking-tight mt-3">
                  Everything you need to protect your business name
                </h2>
              </div>
            </div>
            <ul className="grid md:grid-cols-2 gap-3 mt-6">
              {EVERY_PLAN.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-[#447694]" data-testid="pricing-every-plan-item">
                  <Check size={16} className="text-[#0d9b80] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHICH PLAN SHOULD YOU CHOOSE */}
      <section className="py-12 bg-gradient-to-br from-[#f7f7f7] via-white to-[#71edc9]/10" data-testid="pricing-which-plan-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-2xl mx-auto">
            <span className="bnp-chip bnp-chip-light">Quick decision guide</span>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-[#04273d] mt-3 font-bold tracking-tight">
              Which plan should you choose?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mt-7">
            <div className="bnp-card p-6 md:p-7 flex gap-4 items-start">
              <div className="h-10 w-10 rounded-xl bg-[#04273d]/5 flex items-center justify-center shrink-0">
                <Wallet size={18} className="text-[#04273d]" />
              </div>
              <div>
                <p className="font-display font-bold text-[#04273d]">If you want flexibility</p>
                <p className="text-sm text-[#447694] mt-1.5 leading-relaxed">Choose monthly.</p>
              </div>
            </div>
            <div className="bnp-card p-6 md:p-7 flex gap-4 items-start border-[#71edc9]/40">
              <div className="h-10 w-10 rounded-xl bg-[#71edc9]/20 flex items-center justify-center shrink-0">
                <Sparkles size={18} className="text-[#0d9b80]" />
              </div>
              <div>
                <p className="font-display font-bold text-[#04273d]">If you want the best value and fewer payments</p>
                <p className="text-sm text-[#447694] mt-1.5 leading-relaxed">Choose annual.</p>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-[#447694] mt-6 leading-relaxed">
            Both give you the same protection.
            {" "}<span className="text-[#04273d] font-semibold">Most customers choose the annual plan</span> for simplicity and savings.
          </p>
        </div>
      </section>

      {/* PRICING QUESTIONS (FAQ) */}
      <section className="py-12 bg-white border-y border-[#9ac7f7]/25" data-testid="pricing-faq-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <h2 className="font-display text-2xl md:text-3xl text-[#04273d] font-semibold tracking-tight">Pricing questions</h2>
          <p className="text-sm text-[#447694] mt-2">Tap a question to reveal the answer.</p>
          <div className="mt-5 space-y-3">
            {FAQ.map((f, i) => (
              <details
                key={i}
                className="bnp-card group p-0 overflow-hidden [&_summary::-webkit-details-marker]:hidden"
                data-testid={`pricing-faq-${i}`}
              >
                <summary
                  className="cursor-pointer list-none flex items-center justify-between gap-4 p-5 md:p-6 select-none"
                  data-testid={`pricing-faq-${i}-question`}
                >
                  <span className="font-display font-bold text-[#04273d]">{f.q}</span>
                  <ChevronDown
                    size={20}
                    className="text-[#04273d] shrink-0 transition-transform duration-200 group-open:rotate-180"
                  />
                </summary>
                <div
                  className="px-5 md:px-6 pb-5 md:pb-6 -mt-1 space-y-2 border-t border-[#9ac7f7]/25 pt-4"
                  data-testid={`pricing-faq-${i}-answer`}
                >
                  {f.a.map((para, k) => (
                    <p key={k} className="text-sm text-[#447694] leading-relaxed">{para}</p>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* NOT SURE YET - free check */}
      <section className="py-16" data-testid="pricing-not-sure-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center">
          <span className="bnp-chip bnp-chip-teal">Not sure yet?</span>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-[#04273d] mt-3 font-bold tracking-tight">
            Run a free name check first to see if your business name may be at risk
          </h2>
          <p className="text-[#447694] mt-3 leading-relaxed">
            Enter your business name and get instant guidance.
          </p>
          <div className="mt-6">
            <HeroSearch inline />
          </div>
          <div className="mt-7">
            <Link to="/check" className="btn-secondary" data-testid="pricing-full-check-link">
              Or go to the full check page <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA - three-button row */}
      <section className="py-16 bg-[#04273d] text-white" data-testid="pricing-final-cta">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center">
          <Scale size={28} className="text-[#71edc9] mx-auto" />
          <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] lg:text-5xl mt-4 font-bold tracking-tight leading-[1.08]">
            Protect your business name today
          </h2>
          <p className="text-[#9ac7f7] mt-3 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
            Choose the plan that fits your business. Same protection either way.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/checkout/details?plan=monthly"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#04273d] hover:bg-[#71edc9] font-display font-bold px-6 py-3.5 rounded-full transition-colors w-full sm:w-auto"
              data-testid="pricing-final-monthly-button"
            >
              Start monthly protection <ArrowRight size={14} />
            </Link>
            <Link
              to="/checkout/details?plan=annual"
              className="inline-flex items-center justify-center gap-2 bg-[#71edc9] text-[#04273d] hover:bg-[#71edc9]/85 font-display font-bold px-6 py-3.5 rounded-full transition-colors w-full sm:w-auto"
              data-testid="pricing-final-annual-button"
            >
              Choose annual protection <ArrowRight size={14} />
            </Link>
          </div>
          <p className="text-[11.5px] text-[#9ac7f7] mt-5 font-mono-brand uppercase tracking-widest">
            VAT included &middot; No setup fees &middot; Cancel any time
          </p>
        </div>
      </section>
    </main>
  );
}
